import React from 'react';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';
import { Pagination } from 'antd';
import styled from 'styled-components';

import { formatScore, statusLabel } from '../../../cm/utils/framlSummary';
import { formatTypologyNames } from '../../../../util/typology';
import { convertISOToDateFormat } from '../../../../util/timezone';

// FIN-11360 — expanded underlying-alerts view, styled to the FRAML View design
// language (PRD §10 columns + §6 helper note).
// FIN-12887/12892: server-paged — `alerts` is ONE page; `total`/`page`/`size` come
// from the BE envelope and drive the header count + the pager.
const Wrap = styled.div`
  background: #f9fafe;
  border: 1px solid #eef0f5;
  border-radius: 8px;
  padding: 14px 16px;
`;
const Head = styled.div`
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 10px;
`;
const Heading = styled.div`
  font-size: 13px;
  font-weight: 700;
  color: #1f2430;
`;
const Note = styled.div`
  font-size: 11.5px;
  color: #8a909c;
  font-style: italic;
`;
// FIN-12888: fixed table layout + wrapping cells — the auto layout let a long
// typology string stretch its column and (with an inherited white-space:nowrap)
// push the table past the expand panel. Fixed layout honours the header widths;
// break-word keeps unbroken tokens (alert ids, joined typology lists) inside.
const Tbl = styled.table`
  width: 100%;
  table-layout: fixed;
  border-collapse: collapse;
  font-size: 12.5px;
  th {
    text-align: left;
    color: #8a909c;
    font-weight: 600;
    font-size: 10.5px;
    letter-spacing: 0.03em;
    text-transform: uppercase;
    padding: 6px 10px;
    border-bottom: 1px solid #e8eaf0;
  }
  td {
    padding: 8px 10px;
    border-bottom: 1px solid #f0f1f5;
    color: #1f2430;
    white-space: normal;
    overflow-wrap: break-word;
    word-break: break-word;
    vertical-align: top;
  }
  tr:last-child td {
    border-bottom: none;
  }
`;
// FIN-13951: pills stay on one line — the td's break-word wrapping split long admin-configured
// status names ("Work in / progress") and misaligned rows. Overflow ellipsises; the full name
// is on the status pill's title.
const Pill = styled.span`
  display: inline-block;
  max-width: 100%;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  vertical-align: top;
  padding: 1px 8px;
  border-radius: 999px;
  font-size: 11px;
  font-weight: 500;
  background: ${(p) => p.bg};
  color: ${(p) => p.fg};
`;
const Empty = styled.div`
  color: #8a909c;
  font-size: 12.5px;
  padding: 10px 0;
`;
// FIN-13005 — inline retry beside the failure message; the expand fetch is per-customer, so a
// transient 500 on one row shouldn't require collapsing and re-expanding.
const RetryButton = styled.button`
  margin-left: 8px;
  background: none;
  border: none;
  padding: 0;
  color: #2f6fd0;
  font-size: 12.5px;
  cursor: pointer;
  text-decoration: underline;
`;
const Foot = styled.div`
  display: flex;
  justify-content: flex-end;
  padding-top: 10px;
`;

const SRC_TONE = {
  Fraud: { bg: '#fde8e6', fg: '#d6453a' },
  AML: { bg: '#e3edff', fg: '#2f6df6' }
};
const sourcePill = (s) => {
  const t = SRC_TONE[s] || { bg: '#eef0f4', fg: '#6b7280' };
  return (
    <Pill bg={t.bg} fg={t.fg}>
      {s}
    </Pill>
  );
};
// FIN-12897 / FIN-12916: statusLabel lives in the pure cm/utils/framlSummary module so shared
// screens (common/TransactionDetalis/ExportHistory) can use it without importing this
// styled component module. Re-exported here for existing callers.
export { statusLabel };
const statusPill = (code, list) => {
  const label = statusLabel(code, list);
  return (
    <Pill bg="#eef0f4" fg="#6b7280" title={label}>
      {label}
    </Pill>
  );
};

// FIN-12919: highlight expand rows whose typology matches the queue's active search
// text, so the analyst can see WHY a searched customer matched.
const rowMatches = (row, highlight) => {
  const h = (highlight || '').trim().toLowerCase();
  if (!h) return false;
  return (row.typology || '').toLowerCase().includes(h);
};

// FIN-13008 — records-per-page choices, the same set the Activity Events tab offers.
// The CM BE clamps `size` to 100 (FramlQueueService.MaxAlertsPageSize), so 100 is the ceiling.
const DEFAULT_PAGE_SIZE = 20;
const PAGE_SIZE_OPTIONS = [10, 20, 50, 100];

function FramlUnderlyingAlerts({
  alerts,
  total,
  page,
  size,
  onPageChange,
  workFlowStatusList,
  highlight,
  // FIN-13005 — set when the expand FETCH FAILED. Distinct from an empty result: previously the
  // saga swallowed the error, so a failing endpoint rendered the empty state and counted (0),
  // which reads as "this customer has no underlying alerts".
  error,
  onRetry
}) {
  const count = total ?? (alerts ? alerts.length : 0);
  const pageSize = size || DEFAULT_PAGE_SIZE;
  // FIN-13008 — gate on the SMALLEST page-size option, not the current size: otherwise a
  // customer whose alerts fit the current page could never reach the size changer (and after
  // picking 50 there'd be no way back down to 10).
  const showPager = onPageChange && count > Math.min(...PAGE_SIZE_OPTIONS);
  const isEmpty = !alerts || alerts.length === 0;
  return (
    <Wrap>
      <Head>
        <Heading>
          {/* Don't assert a count we failed to load — "(0)" would be a claim, not a fact. */}
          Underlying Alerts{error && isEmpty ? '' : ` (${count})`}
        </Heading>
        <Note>
          Per-alert score is the raw signal — parent Fraud Heat is the
          time-decayed aggregate.
        </Note>
      </Head>
      {error && isEmpty ? (
        <Empty>
          Couldn&apos;t load underlying alerts.
          {onRetry && (
            <RetryButton type="button" onClick={onRetry}>
              Retry
            </RetryButton>
          )}
        </Empty>
      ) : isEmpty ? (
        // FIN-14027 — the list is scoped to what the caller may open (FIN-13972), so an empty
        // list is not proof the customer has no alerts. Product-confirmed wording (Yogesh).
        <Empty>
          No underlying alert or you do not have permission to view the
          underlying alerts
        </Empty>
      ) : (
        <Tbl>
          <thead>
            <tr>
              <th scope="col" style={{ width: 72 }}>
                Domain
              </th>
              <th scope="col" style={{ width: 190 }}>
                Alert ID
              </th>
              <th scope="col" style={{ width: 90 }}>
                Category
              </th>
              {/* FIN-12886: fraud alerts carry variant names in this column — label both.
                  FIN-13951: the only auto-width column — every other column is fixed px, so
                  widths stay consistent across panel sizes and only this one absorbs slack. */}
              <th scope="col">Typology/Variant</th>
              <th scope="col" style={{ width: 64 }}>
                Score
              </th>
              <th scope="col" style={{ width: 100 }}>
                Date
              </th>
              <th scope="col" style={{ width: 150 }}>
                Status
              </th>
            </tr>
          </thead>
          <tbody>
            {alerts.map((row) => (
              <tr
                key={row.alertId}
                data-highlighted={
                  rowMatches(row, highlight) ? 'true' : undefined
                }
                style={
                  rowMatches(row, highlight)
                    ? { background: '#fff7e0' }
                    : undefined
                }>
                <td>{sourcePill(row.source)}</td>
                <td>
                  <Link to={`/app/cm/alerts/${row.alertId}`}>
                    {row.alertId}
                  </Link>
                </td>
                {/* FIN-13013 — generated underlying_category, verbatim:
                    TXN (fraud txn), ACT (fraud activity), CUST (AML). */}
                <td>{row.category}</td>
                {/* FIN-13015 — typologyName is stored with a baked-in "Typology" prefix
                    ("TypologyHigh count of..."); this is a joined list (fraud rows carry
                    variant names too), so reuse the shared joined-list stripper rather than
                    a naive single-name strip. */}
                <td>{formatTypologyNames(row.typology)}</td>
                <td
                  style={{
                    fontWeight: 600,
                    fontVariantNumeric: 'tabular-nums'
                  }}>
                  {/* FIN-13932 — the BE sends the live alert.score; render it with the CM formatter
                      (FIN-13184: 2 dp, clamped) so it reads as on the case page, not raw 3-dp. */}
                  {formatScore(row.score)}
                </td>
                {/* FIN-13010 — row.date arrives as a raw ISO string; apply the shared
                    date formatter used across CM (convertISOToDateFormat). */}
                <td style={{ color: '#6b7280' }}>
                  {row.date ? convertISOToDateFormat(row.date) : ''}
                </td>
                <td>{statusPill(row.status, workFlowStatusList)}</td>
              </tr>
            ))}
          </tbody>
        </Tbl>
      )}
      {showPager && (
        <Foot>
          <Pagination
            size="small"
            current={(page || 0) + 1}
            pageSize={pageSize}
            total={count}
            // FIN-13008 — explicit: antd v4 only auto-shows the changer when total > 50.
            showSizeChanger
            pageSizeOptions={PAGE_SIZE_OPTIONS}
            // A size change restarts at the first page (0-based): the old page index is a
            // different offset under the new size and could point past the end.
            onChange={(p, s) =>
              s && s !== pageSize
                ? onPageChange(0, s)
                : onPageChange(p - 1, pageSize)
            }
          />
        </Foot>
      )}
    </Wrap>
  );
}

FramlUnderlyingAlerts.propTypes = {
  alerts: PropTypes.array,
  total: PropTypes.number,
  page: PropTypes.number,
  size: PropTypes.number,
  onPageChange: PropTypes.func,
  workFlowStatusList: PropTypes.array,
  highlight: PropTypes.string,
  error: PropTypes.string,
  onRetry: PropTypes.func
};

FramlUnderlyingAlerts.defaultProps = {
  alerts: [],
  total: undefined,
  page: 0,
  size: 20,
  onPageChange: undefined,
  workFlowStatusList: [],
  highlight: '',
  error: undefined,
  onRetry: undefined
};

export default FramlUnderlyingAlerts;
